import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import ProjectEditor from '../widget/ProjectEditor.js';
import ObjectHelper from '../../Core/helper/ObjectHelper.js';
import TaskEditBase from '../../SchedulerPro/feature/TaskEditBase.js';

/**
 * @module Gantt/feature/ProjectEdit
 */

/**
 * Feature that displays the {@link Gantt/widget/ProjectEditor project editor} allowing users to edit
 * the project settings.
 *
 * This demo shows the project edit feature, click "Edit project" button to start editing:
 *
 * {@inlineexample Gantt/feature/ProjectEdit.js}
 *
 * ## Customizing tabs and their widgets
 *
 * To customize tabs you can:
 *
 * * Reconfigure built-in tabs by providing override configs in the {@link #config-items} config.
 * * Remove existing tabs or add your own in the {@link #config-items} config.
 * * Advanced: Reconfigure the whole editor widget using {@link #config-editorConfig} or replace the whole editor
 *   using {@link #config-editorClass}.
 *
 * This example shows a custom Project Editor configuration. The built-in "Description" tab is hidden,
 * the "General" tab is renamed to "Common" and "Custom" field is appended to it.
 * Click "Edit project" button to start editing::
 *
 * {@inlineexample Gantt/feature/ProjectEditCustom.js}
 *
 * {@region Expand to see Default tabs and fields}
 *
 * The {@link Gantt/widget/ProjectEditor project editor} contains tabs by default. Each tab is a container with built
 * in widgets.
 *
 * | Tab ref          | Type                                                          | Text         | Weight | Description                                                                       |
 * |------------------|---------------------------------------------------------------|--------------|--------|-----------------------------------------------------------------------------------|
 * | `generalTab`     | {@link Gantt/widget/projecteditor/ProjectEditorGeneralTab}    | General      | 100    | Shows basic configuration: name, start/end dates, schedule form                   |
 * | `descriptionTab` | {@link Gantt/widget/projecteditor/ProjectEditorDescriptionTab}| Description  | 200    | Shows a text area to add a description to the edited project                      |
 * | `advancedTab`    | {@link Gantt/widget/projecteditor/ProjectEditorAdvancedTab}   | Advanced     | 300    | Shows advanced options: number of hours per day, days per week and days per month |
 *
 * ### General tab
 *
 * General tab contains widgets for basic configurations
 *
 * | Field ref           | Type                                       | Weight | Description                                                                          |
 * |---------------------|--------------------------------------------|--------|--------------------------------------------------------------------------------------|
 * | `nameField`         | {@link Core/widget/TextField}              | 100    | Project {@link Gantt/model/ProjectModel#field-name name} field                       |
 * | `startDateField`    | {@link SchedulerPro/widget/StartDateField} | 200    | Project {@link Gantt/model/ProjectModel#field-startDate start date} field            |
 * | `endDateField`      | {@link SchedulerPro/widget/EndDateField}   | 300    | Project {@link Gantt/model/ProjectModel#field-endDate end date} field                |
 * | `calendarField`     | {@link SchedulerPro/widget/CalendarField}  | 400    | Project {@link Gantt/model/ProjectModel#field-calendar} field                        |
 * | `scheduleFromField` | {@link Core/widget/RadioGroup}             | 500    | Project {@link Gantt/model/ProjectModel#field-direction scheduling direction} field  |
 * | `readOnlyField`     | {@link Core/widget/Checkbox}               | 600    | Project {@link Gantt/model/ProjectModel#field-readOnly} field                        |
 *
 * ### Advanced tab
 *
 * A tab showing the advanced options for a project.
 *
 * | Field ref           | Type                            | Weight | Description                                                                      |
 * |---------------------|---------------------------------|--------|----------------------------------------------------------------------------------|
 * | `hoursPerDayField`  | {@link Core/widget/NumberField} | 100    | Project {@link Gantt/model/ProjectModel#field-hoursPerDay hours per day} field |
 * | `daysPerWeekField`  | {@link Core/widget/NumberField} | 200    | Project {@link Gantt/model/ProjectModel#field-daysPerWeek days per week} field   |
 * | `daysPerMonthField` | {@link Core/widget/NumberField} | 300    | Project {@link Gantt/model/ProjectModel#field-daysPerMonth days per month} field |
 *
 * ### Description tab
 *
 * Notes tab contains a text area to show notes
 *
 * A tab showing the description for a project.
 *
 * | Field ref          | Type                          | Weight | Description                                           |
 * |--------------------|-------------------------------|--------|-------------------------------------------------------|
 * | `descriptionField` | {@link Core/widget/TextField} | 100    | A textarea field, to set a description to the project |
 *
 * {@endregion}
 *
 * ## Removing a built-in item
 *
 * To remove a built-in tab or widget, specify its `ref` as `false` in the {@link #config-items} config:
 *
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         projectEdit : {
 *             items : {
 *                 generalTab  : {
 *                     items : {
 *                         // Remove the "Calendar" field in the "General" tab
 *                         calendarField : false
 *                     }
 *                 },
 *                 // Remove all tabs except the "General" tab
 *                 descriptionTab : false,
 *                 advancedTab    : false
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * The built-in buttons are:
 *
 * | Widget ref     | Type                       | Weight | Description                             |
 * |----------------|----------------------------|--------|-----------------------------------------|
 * | `saveButton`   | {@link Core/widget/Button} | 100    | Save event button on the bbar           |
 * | `cancelButton` | {@link Core/widget/Button} | 300    | Cancel event editing button on the bbar |
 *
 * Bottom buttons may be hidden using `bbar` config passed to `editorConfig`:
 *
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         projectEdit : {
 *             editorConfig : {
 *                 bbar : {
 *                     items : {
 *                         cancelButton : false
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Customizing a built-in item
 *
 * To customize a built-in tab or field, use its `ref` as the key in the {@link #config-items} config and specify the configs you want
 * to change (they will be merged with the tabs or fields default configs correspondingly):
 *
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         projectEdit : {
 *             items : {
 *                 generalTab : {
 *                     // Rename "General" tab
 *                     title : 'Main',
 *                     items : {
 *                         // Rename "Calendar" field
 *                         calendarField : {
 *                             label : 'Work time'
 *                         }
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Adding a custom item
 *
 * To add a custom tab or field, add an entry to the {@link #config-items} config. When you add a field,
 * the `name` property links the input field to a field in the loaded project record:
 *
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         projectEdit : {
 *             items : {
 *                 generalTab : {
 *                     items : {
 *                         // Add new field to the last position
 *                         newGeneralField : {
 *                             type   : 'textfield',
 *                             weight : 710,
 *                             label  : 'New field in General Tab',
 *                             // Name of the field matches data field name, so value is loaded/saved automatically
 *                             name   : 'custom'
 *                         }
 *                     }
 *                 },
 *                 // Add a custom tab to the first position
 *                 newTab     : {
 *                     // Tab is a FormTab by default
 *                     title  : 'New tab',
 *                     // provide weight less than General tab has to make sure this tab goes first
 *                     weight : 90,
 *                     items  : {
 *                         newTabField : {
 *                             type   : 'textfield',
 *                             weight : 710,
 *                             label  : 'New field in New Tab',
 *                             // Name of the field matches data field name, so value is loaded/saved automatically.
 *                             // In this case it is equal to the project "name" field.
 *                             name   : 'name'
 *                         }
 *                     }
 *                 }
 *             }
 *         }
 *     }
 * })
 * ```
 *
 * ## Manipulating ProjectEditor items at run time
 *
 * To change input items depending upon the project being edited, use a {@link #event-beforeProjectEditShow}
 * listener to access the {@link Gantt.widget.ProjectEditor editor} instance.
 *
 * The available widgets are described {@link #expand-to-see-default-tabs-and-fields here}.
 *
 * The {@link Gantt.widget.ProjectEditor editor} exposes all its descendant widgets in its
 * {@link Core.widget.Container#property-widgetMap}.
 *
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         projectEdit : true
 *     },
 *     listeners : {
 *         beforeProjectEditShow({ projectRecord, editor }) {
 *             // if editing a project having non empty startDate value.
 *             if (projectRecord.startDate) {
 *                 // do not allow editing the project start date
 *                 editor.widgetMap.startDate.disabled = true;
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * This feature is **disabled** by default
 *
 * @extends SchedulerPro/feature/TaskEditBase
 * @classtype projectEdit
 * @demo Gantt/advanced
 * @feature
 */
export default class ProjectEdit extends TaskEditBase {

    //region Events
    /**
     * Fires on the owning Gantt widget instance before a project is displayed in the editor.
     * This may be listened to in order to take over the project editing flow. Returning `false`
     * stops the default editing UI from being shown.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new Gantt({
     *     listeners : {
     *         async beforeProjectEdit() {
     *            await asyncCheckOfRightsOnBackend();
     *         }
     *     }
     * });
     * ```
     * @event beforeProjectEdit
     * @on-owner
     * @param {Gantt.view.Gantt} source The Gantt instance
     * @param {Gantt.feature.ProjectEdit} projectEdit The projectEdit feature
     * @param {Gantt.model.ProjectModel} projectRecord The project about to be shown in the editor.
     * @preventable
     * @async
     */

    /**
     * Fires on the owning Gantt widget when the editor for a project is canceled.
     * @event projectEditCanceled
     * @on-owner
     * @param {Gantt.view.Gantt} source The Gantt instance
     * @param {Gantt.model.ProjectModel} projectRecord The edited project
     * @param {Gantt.widget.ProjectEditor} editor The editor
     */

    /**
     * Fires on the owning Gantt widget when the editor for a project is available but
     * before it is shown. Allows manipulating fields etc.
     * @event beforeProjectEditShow
     * @on-owner
     * @param {Gantt.view.Gantt} source The Gantt instance
     * @param {Gantt.feature.ProjectEdit} projectEdit The projectEdit feature
     * @param {Gantt.model.ProjectModel} projectRecord The project about to be shown in the editor.
     * @param {Gantt.widget.ProjectEditor} editor The editor
     */

    /**
     * Fires on the owning Gantt widget instance before a project is saved, return `false` to prevent it.
     *
     * Allows async flows by awaiting async listeners. For example:
     *
     * ```javascript
     * new Gantt({
     *     listeners : {
     *         async beforeProjectSave() {
     *            await someAsyncConditionLikeAskingForConfirmation();
     *         }
     *     }
     * });
     * ```
     *
     * @event beforeProjectSave
     * @on-owner
     * @param {Gantt.view.Gantt} source The Gantt instance
     * @param {Gantt.model.ProjectModel} projectRecord The project about to be saved
     * @param {Gantt.widget.ProjectEditor} editor The editor widget
     * @preventable
     * @async
     */

    /**
     * Fires on the owning Gantt widget instance after a project is saved
     * @event afterProjectSave
     * @on-owner
     * @param {Gantt.view.Gantt} source The Gantt instance
     * @param {Gantt.model.ProjectModel} projectRecord The project about to be saved
     * @param {Gantt.widget.ProjectEditor} editor The editor widget
     */

    /**
     * Fires on the owning Gantt widget instance after project editing is finished by applying changes or
     * cancelling them.
     * @event afterProjectEdit
     * @on-owner
     * @param {Gantt.view.Gantt} source The Gantt instance
     * @param {Gantt.model.ProjectModel} projectRecord The edited project record
     * @param {Gantt.widget.ProjectEditor} editor The editor widget
     */

    //endregion

    //region Config

    static $name = 'ProjectEdit';

    static get pluginConfig() {
        return {
            assign : ['editProject']
        };
    }

    static get configurable() {
        return {
            entityName : 'project',

            /**
             * A configuration object used to customize the contents of the project editor.
             * Supply a config object or boolean per tab (listed below) to either affects
             * its contents or toggle it on/off.
             *
             * Supplied config objects will be merged with the tabs predefined configs.
             *
             * To remove existing items, set corresponding keys to `null`.
             *
             * Built-in tab names are:
             *
             * - `generalTab`
             * - `descriptionTab`
             * - `advancedTab`
             *
             * ```
             * features : {
             *     projectEdit : {
             *         items : {
             *             // Custom settings and additional items for the general tab
             *             generalTab : {
             *                 title : 'Common',
             *                 items : {
             *                     // hide project name field
             *                     nameField : false,
             *                     // add a new custom text field
             *                     myCustomField : {
             *                         type : 'text',
             *                         name : 'color'
             *                     }
             *                 }
             *             },
             *             // Hide the advanced tab
             *             advancedTab : null
             *         }
             *     }
             * }
             * ```
             * @config {Object} items
             */

            disableEditorForReadOnlyRecord : false,

            /**
             * A configuration object applied to the internal {@link Gantt/widget/ProjectEditor}.
             * Useful to for example change the title of the editor or to set its dimensions in code:
             *
             * ```javascript
             * features : {
             *     projectEdit : {
             *         editorConfig : {
             *             title : 'My title',
             *             height : 300
             *         }
             *     }
             * }
             * ```
             *
             * NOTE: The easiest approach to affect editor contents is to use the {@link #config-items items config}.
             *
             * @config {ProjectEditorConfig}
             */
            editorConfig : {
                type : ProjectEditor.type
            }
        };
    }

    //endregion

    //region Constructor/Destructor

    construct(client, config) {
        client.projectEdit = this;

        super.construct(client, config);
    }

    //endregion

    //region Editor

    /**
     * Shows a {@link Gantt.widget.ProjectEditor project editor}
     * to edit the passed project.
     * This function is exposed on the Gantt instance and can be called as `gantt.editProject()`.
     * @param {Gantt.model.ProjectModel|Function} [project] Project to edit or a function returning a
     * project to edit, the function will be executed within an STM transaction which will be canceled in case
     * user presses "Cancel" button or closes editor w/o hitting "Save".
     * If the argument is not provided the method will oad the `Gantt.project`.
     * @param {HTMLElement} [element] Element to anchor the editor to
     * @returns {Promise} Promise which resolves after the editor is shown
     */
    async editProject(project = this.client.project, element = null, stmCapture = null) {
        return this.editRecord(project, element, stmCapture);
    }

    prepareEditorConfig(config) {
        ObjectHelper.merge(config, {
            projectEditFeature : this,
            weekStartDay       : this.weekStartDay
        });

        return super.prepareEditorConfig(config);
    }

    getEditor(record = this.record, cfg) {
        const result = super.getEditor(...arguments);

        result.project = record;

        return result;
    }

    //endregion

}

GridFeatureManager.registerFeature(ProjectEdit, false, 'Gantt');
GridFeatureManager.registerFeature(ProjectEdit, false, 'ResourceHistogram');
